import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { toIsoStringOrNull } from '@/components/sessions/DateTimePicker';
import { SessionPageHeader } from '@/components/sessions/SessionPageHeader';
import { SessionForm, type SessionFormData } from '@/components/sessions/SessionForm';
import { sessionService } from '@/services/sessionService';
import { useAuth } from '@/providers/AuthProvider';
import type { PageProps } from '@/types/page';
import type { CreateSessionRequest } from '@arrowise/shared';

export default function CreateSessionPage({ nav = true, footer = true }: PageProps) {
    const navigate = useNavigate();
    const { teams: authTeams, getUserTeamRole } = useAuth();

    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const inTeam = authTeams.length > 0;
    const canAdmin = useMemo(() => {
        if (!inTeam) return false;
        const role = getUserTeamRole(authTeams[0].team.id);
        return role === 'admin' || role === 'owner';
    }, [authTeams, inTeam, getUserTeamRole]);

    // Form data for individual sessions
    const [individualFormData, setIndividualFormData] = useState<SessionFormData>({
        dueAt: null,
        candidate: { name: '', email: '' },
        selectedTemplate: null,
        selectedPosition: null
    });

    // Form data for team sessions
    const [teamFormData, setTeamFormData] = useState<SessionFormData>({
        dueAt: null,
        candidate: { name: '', email: '' },
        selectedTemplate: null,
        selectedPosition: null
    }); async function handleIndividualSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!individualFormData.candidate.name || !individualFormData.candidate.email) {
            toast.error('Candidate name and email are required');
            return;
        }
        if (!individualFormData.selectedTemplate?.id) {
            toast.error('Please select a template');
            return;
        }
        if (!individualFormData.selectedPosition?.id) {
            toast.error('Please select a position');
            return;
        }

        setLoading(true);
        try {
            const payload: CreateSessionRequest = {
                language: 'javascript',
                candidate: individualFormData.candidate,
                dueAt: toIsoStringOrNull(individualFormData.dueAt) || undefined,
                templateId: individualFormData.selectedTemplate.id,
                positionId: individualFormData.selectedPosition.id,
            };
            await sessionService.createSession(payload);
            toast.success('Session created');
            navigate('/app/sessions');
        } catch (err: any) {
            const message = err?.message || 'Failed to create session';
            if (typeof message === 'string' && message.includes('AI provider key not configured')) {
                toast.error('AI key required to create a session', {
                    description: 'Set up your AI provider key in Settings → AI Keys.',
                    action: {
                        label: 'Open Settings',
                        onClick: () => navigate('/app/settings?tab=keys')
                    }
                });
            } else {
                toast.error(message);
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleTeamSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!canAdmin) {
            toast.error('Only team admins/owners can create sessions');
            return;
        }
        if (!teamFormData.dueAt) {
            toast.error('Select a due date');
            return;
        }
        if (!teamFormData.candidate.name || !teamFormData.candidate.email) {
            toast.error('Provide candidate name and email');
            return;
        }
        if (!teamFormData.selectedTemplate?.id) {
            toast.error('Please select a template');
            return;
        }
        if (!teamFormData.selectedPosition?.id) {
            toast.error('Please select a position');
            return;
        }

        setLoading(true);
        try {
            const payload: CreateSessionRequest = {
                dueAt: toIsoStringOrNull(teamFormData.dueAt) || undefined,
                language: 'javascript',
                templateId: teamFormData.selectedTemplate.id,
                teamId: authTeams[0].team.id,
                candidate: teamFormData.candidate,
                positionId: teamFormData.selectedPosition.id,
            };
            await sessionService.createSession(payload);
            toast.success('Team session created');
            navigate('/app/sessions');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to create team session');
        } finally {
            setLoading(false);
        }
    }

    const isIndividualSubmitDisabled = !individualFormData.candidate.name ||
        !individualFormData.candidate.email ||
        !individualFormData.dueAt ||
        !individualFormData.selectedPosition?.id;

    const isTeamSubmitDisabled = !canAdmin ||
        !authTeams[0]?.team.id ||
        !teamFormData.dueAt ||
        !teamFormData.candidate.name ||
        !teamFormData.candidate.email ||
        !teamFormData.selectedPosition?.id;

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-4xl w-full space-y-8">
                <SessionPageHeader
                    title="Create Session"
                    description="Set up a new interview session."
                />

                {!inTeam && (
                    <Card>
                        <CardContent>
                            <SessionForm
                                data={individualFormData}
                                onDataChange={setIndividualFormData}
                                onSubmit={handleIndividualSubmit}
                                teamId={null}
                                candidateEditable={true}
                                useParticipantsEditor={false}
                                submitLabel="Create"
                                submitDisabled={isIndividualSubmitDisabled}
                                loading={loading}
                                templateDialogOpen={templateDialogOpen}
                                onTemplateDialogOpenChange={setTemplateDialogOpen}
                                showDueDate={true}
                                dueDateRequired={false}
                            />
                        </CardContent>
                    </Card>
                )}

                {inTeam && (
                    <Card>
                        <CardContent>
                            <SessionForm
                                data={teamFormData}
                                onDataChange={setTeamFormData}
                                onSubmit={handleTeamSubmit}
                                teamId={authTeams[0]?.team.id}
                                candidateEditable={true}
                                useParticipantsEditor={true}
                                submitLabel="Create"
                                submitDisabled={isTeamSubmitDisabled}
                                loading={loading}
                                templateDialogOpen={templateDialogOpen}
                                onTemplateDialogOpenChange={setTemplateDialogOpen}
                                showDueDate={true}
                                dueDateRequired={true}
                            />
                        </CardContent>
                    </Card>
                )}
            </div>
        </PageLayout>
    );
}
